import { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { NavigateOptions, useLocation } from 'react-router-dom';
import { startWith, withLatestFrom } from 'rxjs';
import { NavigateType } from '@/components/SideBar/SideBar';
import { Dynamics } from '@/services/dynamics/dynamics';
import { EModeValue } from '@/services/dynamics/enums/mode.enum';
import { SizerState } from '@/services/sizer-state.service';
import { useStore } from '@/stores';
import { ECallStatus } from '@/shared/enums/ringing-status.enum';
import { CALL_PAGE_TYPE } from '@/shared/constants/callPage';
import { CallPageType } from '@/shared/interfaces/call-page.interface';
import { ErrorContext } from '@/components/ErrorContextProvider/ErrorContextProvider';
import { SipJs } from '@/services/sip-js.service';
import { logger } from '@/services/logger.service';

export const useAppNavigate = ({ navigate, appRef }) => {
  const [showSideBar, setShowSideBar] = useState(false);
  const { updateError } = useContext(ErrorContext);
  // This setting is needed to get access to current route inside a subscription(location and store objects are out of scope)
  // forceCallOpen flag is required to open current call from minimized state despite of being on a particular route
  const routeRef = useRef({ route: '', forceCallOpen: false });

  const {
    callStore: { setCallPage, setWidgetMode, wrapUpSession },
  } = useStore();

  const location = useLocation();

  const checkAndOpenIfPluginMinimized = async (forceCallOpen = false) => {
    routeRef.current.forceCallOpen = forceCallOpen;
    const mode = await Dynamics.getMode();
    if (mode === EModeValue.DOCK) {
      setViewForPlugin('show', 'show');
    } else {
      await Dynamics.setMode(EModeValue.DOCK);
    }
  };

  const setViewForPlugin = (panel: string, sidebar: string) => {
    switch (sidebar) {
      case 'show': {
        setShowSideBar(true);
        break;
      }
      case 'hide': {
        setShowSideBar(false);
        break;
      }
    }
  };

  const navigateTo = (navigateType: NavigateType, opts?: NavigateOptions) => {
    logger.log(`[ActivityHistory] Navigate to: ${navigateType}`);
    updateError(false);
    routeRef.current.route = navigateType;
    switch (navigateType) {
      case 'logout':
        SipJs.end();
        break;
      case navigateType:
        navigate.current(`/${navigateType}`, opts);
        break;
      default:
        navigate.current('/dial-pad', opts);
        break;
    }

    checkAndOpenIfPluginMinimized();

    if (SipJs.sessions.length > 0 || wrapUpSession) {
      setCallPage({ page: CALL_PAGE_TYPE.MINIMIZED });
    }
  };

  const setPageViewState = (state: CallPageType, callStatus: ECallStatus) => {
    const { route, forceCallOpen } = routeRef.current;
    const isActiveCallStatus = [
      ECallStatus.CONNECTED,
      ECallStatus.RINGING_IN,
      ECallStatus.RINGING_OUT,
    ].includes(callStatus);
    if (isActiveCallStatus) {
      const page: CallPageType =
        route && !forceCallOpen ? CALL_PAGE_TYPE.MINIMIZED : state;
      setCallPage({ page });
    } else {
      setCallPage({ page: CALL_PAGE_TYPE.HIDDEN });
    }
    routeRef.current.forceCallOpen = false;
  };

  useEffect(() => {
    const sub$ = SizerState.getModeChangeState()
      .pipe(
        withLatestFrom(
          SipJs.callStatus$.pipe(
            startWith(null), // Provide a fallback value if callStatus is not available
          ),
        ),
      )
      .subscribe(([mode, callStatus]) => {
        // Please note that this subscriber can return mode as null at least
        switch (mode) {
          case EModeValue.DOCK:
            if (callStatus) {
              setPageViewState('full', callStatus);
            }
            appRef.current.classList.remove('minimized');
            setWidgetMode(EModeValue.DOCK);
            break;
          case EModeValue.MINIMIZE:
            if (callStatus) {
              setPageViewState('minimized', callStatus);
            }
            appRef.current.classList.add('minimized');
            setWidgetMode(EModeValue.MINIMIZE);
            break;
        }
      });

    return () => {
      sub$.unsubscribe();
    };
  }, []);

  const shouldSidebarRender = useMemo(() => {
    return showSideBar && !location.pathname.includes('inactive');
  }, [showSideBar, location]);

  return {
    navigateTo,
    checkAndOpenIfPluginMinimized,
    setViewForPlugin,
    showSideBar: shouldSidebarRender,
    location,
  };
};
